import { logger, sanitizeError } from '@heidi/runtime/logger';
import { AxiosError } from 'axios';
import { type MessageDescriptor } from 'react-intl';
import { classifyTemplateSaveError } from '../classifyTemplateSaveError';

type ComplianceErrorData = {
  detail: {
    type: string;
    noncompliant_input: string;
    passed_compliance_check: boolean;
  };
};

/**
 * The exact analytics surface this helper uses — a NARROW structural type, not
 * Rematch's `Dispatch` (S1, ARCFE-1017). The store type was this file's only
 * blocker; naming just the two methods it calls removes the coupling without
 * changing a single call site, since the real dispatch structurally satisfies it.
 *
 * Deliberately NOT exported: nothing outside this file references it, and an
 * exported symbol here would be a 57th entry in the templates/crud legacy
 * ratchet, which is shrink-only. Callers keep passing their real dispatch and
 * it structurally satisfies this shape. Export it only if a call site ever
 * needs to name the port — and add the baseline row in the same commit.
 */
type TemplateComplianceAnalytics = {
  analyticsScribe: {
    trackNhsTemplateComplianceWarning: (props: Record<string, unknown>) => void;
    trackNhsTemplateComplianceBlock: (props: Record<string, unknown>) => void;
  };
};

type HandleTemplateComplianceErrorParams = {
  error: unknown;
  templateContent: string;
  templateSource: 'create' | 'update';
  /** Absent on create — the template does not exist yet. */
  templateId?: string;
  dispatch: TemplateComplianceAnalytics;
  formatMessage: (descriptor: MessageDescriptor) => string;
  toast: (params: { tone?: 'destructive'; title: string }) => void;
  setIsRegulatoryGuidelineBlockModalOpen: (open: boolean) => void;
  setIsRegulatoryGuidelineWarningModalOpen: (open: boolean) => void;
  setIsRegulatoryGuidelineWarningModalV2Open: (open: boolean) => void;
  /**
   * The two S4 flags, as GETTERS rather than values (design.md S4: "app-resolved
   * parameter/port per getter").
   *
   * Both resolve to `posthog.isFeatureEnabled`, which emits a
   * `$feature_flag_called` exposure event. The legacy body only reached them
   * inside the compliance branch, so evaluating them eagerly at the call site
   * would fire two exposure events on EVERY template save failure — aborted
   * requests and generic errors included. Passing the getter keeps the
   * evaluation exactly where it was.
   */
  isNhsCompV2Enabled: () => boolean | undefined;
  isTemplateCreationRecordEnabled: () => boolean | undefined;
};

/**
 * Handles compliance-related errors for template creation and updates.
 * Returns true if the error was a compliance error (handled), false otherwise.
 */
export function handleTemplateComplianceError({
  error,
  templateContent,
  templateSource,
  templateId,
  dispatch,
  formatMessage,
  toast,
  setIsRegulatoryGuidelineBlockModalOpen,
  setIsRegulatoryGuidelineWarningModalOpen,
  setIsRegulatoryGuidelineWarningModalV2Open,
  isNhsCompV2Enabled,
  isTemplateCreationRecordEnabled,
}: HandleTemplateComplianceErrorParams): boolean {
  // Skip error handling for aborted requests
  if (error instanceof AxiosError && error.code === 'ERR_CANCELED') {
    return true;
  }

  if (error instanceof AxiosError && error.response?.data) {
    const errorData = error.response.data as ComplianceErrorData;

    if (errorData.detail?.type === 'ComplianceCheckFailedError') {
      const isWarning = errorData.detail.passed_compliance_check;

      const complianceProps = {
        compliance_output_length: errorData.detail.noncompliant_input?.length,
        template_content_length: templateContent.length,
        template_id: templateId,
        template_source: templateSource,
      };

      if (isWarning) {
        dispatch.analyticsScribe.trackNhsTemplateComplianceWarning(complianceProps);
      } else {
        dispatch.analyticsScribe.trackNhsTemplateComplianceBlock(complianceProps);
      }

      if (isNhsCompV2Enabled() && isTemplateCreationRecordEnabled()) {
        setIsRegulatoryGuidelineWarningModalV2Open(true);
      } else if (isWarning) {
        setIsRegulatoryGuidelineWarningModalOpen(true);
      } else {
        setIsRegulatoryGuidelineBlockModalOpen(true);
      }

      // Expected 400: warn, not error; lengths only — never the phrase.
      logger.warn('[TEMPLATE_SAVE] mutation blocked', {
        template_id: templateId,
        template_source: templateSource,
        reason: 'compliance_block',
        is_warning: isWarning,
        noncompliant_input_length: errorData.detail.noncompliant_input?.length,
        template_content_length: templateContent.length,
        editor_surface: 'template_builder',
      });

      return true;
    }
  }

  // Generic error handling. The template payload is clinician-authored, so the
  // raw axios error (which serialises its request body) must never be logged.
  //
  // Distinct from the form's `save failed`: this runs in the mutation's
  // onError, and callers using mutateAsync log again in their own catch. The
  // form's catch also wraps non-mutation work, so it cannot be dropped. Count
  // failures on one message, never on the `[TEMPLATE_SAVE]` prefix.
  logger.error('[TEMPLATE_SAVE] mutation failed', {
    template_id: templateId,
    template_source: templateSource,
    reason: classifyTemplateSaveError(error),
    error: sanitizeError(error),
  });
  toast({
    tone: 'destructive',
    title: formatMessage({
      defaultMessage: 'Failed to save template',
      id: 'mWFa1CqqWS',
    }),
  });

  return false;
}
